import React from 'react';
import I18n from 'i18n-js';
import { core_apimessages_ShippingLabelPackage } from '@reverbdotcom/commons/src/gql/graphql';
import { MeasurementSystem } from './shipping_label_package';
import PackageInput from './package_input';
import HelpCard from '../help_card';
import DimensionIllustration from '../../../../images/shipping/box-dimension-illustration.svg';
import { SanitizedRender } from '@reverbdotcom/commons';
import { RCSegmentedControl } from '@reverbdotcom/cadence/components';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { isUSA } from '../../../lib/geo';

export enum PackageAttribute {
  HEIGHT = 'height',
  LENGTH = 'length',
  WIDTH = 'width',
  WEIGHT = 'weight',
}

interface IProps {
  shippingPackage: core_apimessages_ShippingLabelPackage;
  measurementSystem: MeasurementSystem;
  updatePackageDimensions: (attributes: core_apimessages_ShippingLabelPackage) => void;
  updateMeasurementSystem?: (measurementSystem: MeasurementSystem) => void;
}

export default function CustomPackageInputs({
  updateMeasurementSystem,
  updatePackageDimensions,
  measurementSystem,
  shippingPackage,
}: IProps) {
  const user = useUser();
  const showImperialSystemFirst = isUSA(user.shopCountryCode);

  const usingImperial = measurementSystem === MeasurementSystem.IMPERIAL;
  const usingMetric = measurementSystem === MeasurementSystem.METRIC;

  const ImperialSystemButton = () => (
    <RCSegmentedControl.Button
      key="measurementSystemImperial"
      selected={usingImperial}
      onClick={() => updateMeasurementSystem(MeasurementSystem.IMPERIAL)}
    >
      <SanitizedRender className="text-utility-400" html={I18n.t('discovery.dashboard.selling.shippingLabels.package.imperialUnits')} />
    </RCSegmentedControl.Button>
  );

  const MetricSystemButton = () => (
    <RCSegmentedControl.Button
      key="measurementSystemMetric"
      selected={usingMetric}
      onClick={() => updateMeasurementSystem(MeasurementSystem.METRIC)}
    >
      <SanitizedRender className="text-utility-400" html={I18n.t('discovery.dashboard.selling.shippingLabels.package.metricUnits')} />
    </RCSegmentedControl.Button>
  );

  return (
    <div
      className="g-container g-container--cycle g-container--flex fx-wrap mobile-fx-dir-col">
      <div className="g-col-mobile-12 g-col-6">
        <div className="mb-4">
          <PackageInput
            dimensionKey={PackageAttribute.LENGTH}
            value={shippingPackage.length.value}
            shippingPackage={shippingPackage}
            measurementUnit={shippingPackage.length.unit}
            onUpdate={updatePackageDimensions}
            roundUpUserInput
          />
        </div>
        <div className="mb-4">
          <PackageInput
            dimensionKey={PackageAttribute.WIDTH}
            value={shippingPackage.width.value}
            shippingPackage={shippingPackage}
            measurementUnit={shippingPackage.width.unit}
            onUpdate={updatePackageDimensions}
            roundUpUserInput
          />
        </div>
        <div className="mb-4">
          <PackageInput
            dimensionKey={PackageAttribute.HEIGHT}
            value={shippingPackage.height.value}
            shippingPackage={shippingPackage}
            measurementUnit={shippingPackage.height.unit}
            onUpdate={updatePackageDimensions}
            roundUpUserInput
          />
        </div>
        <PackageInput
          dimensionKey={PackageAttribute.WEIGHT}
          value={shippingPackage.weight.value}
          shippingPackage={shippingPackage}
          measurementUnit={shippingPackage.weight.unit}
          onUpdate={updatePackageDimensions}
        />

        <div className="mt-4">
          <span className="d-block text-default-350 mb-1">{I18n.t('discovery.dashboard.selling.shippingLabels.package.dimensionsSetting')}</span>
          <RCSegmentedControl size="small">
            {showImperialSystemFirst && <ImperialSystemButton />}
            <MetricSystemButton />
            {!showImperialSystemFirst && <ImperialSystemButton />}
          </RCSegmentedControl>
        </div>
      </div>
      <div className="g-col-mobile-12 g-col-6 d-flex mobile-fx-order-3">
        <div className="mobile-fx-width-100 mobile-mb-0 mobile-mt-6">
          <HelpCard
            variant="secondary"
            iconAlt={I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.shippingBoxImgAlt')}
          >
            <div className="text-fine-300">
              <p className="weight-bold text-fine--bold text-default-350 mb-2">
                {I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.makeSure')}
              </p>
              <ul className="rsl-selector-module__list-group">
                <li className="rsl-selector-module__list-item">
                  {usingImperial ?
                    I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.roundUpImperial') :
                    I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.roundUpMetric')
                  }
                </li>
                <li className="rsl-selector-module__list-item">
                  {usingImperial ?
                    I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.addImperial') :
                    I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.addMetric')
                  }
                </li>
              </ul>
              <div className="d-flex fx-justify-center ptb-2">
                <img
                  src={DimensionIllustration}
                  alt={I18n.t('discovery.dashboard.selling.shippingLabels.package.helpCard.boxIllustrationImgAlt')}
                  className="width-80"
                />
              </div>
              <div>
                <p className="mb-2">
                  {I18n.t('discovery.dashboard.selling.shippingLabels.package.inaccurateDimensionsWarning')}
                </p>
                <a
                  href="https://help.reverb.com/hc/articles/360013150694-Why-Did-I-Receive-a-Shipping-Label-Adjustment-#Avoid"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-fine--bold td-underline color-black"
                  tabIndex={-1}
                >
                  {I18n.t('discovery.dashboard.selling.shippingLabels.package.inaccurateDimensionsWarningLink')}
                </a>
              </div>
            </div>
          </HelpCard>
        </div>
      </div>
    </div>
  );
}
